import axios from "axios";
import Format from "string-format";
import { showToast } from "../_utils/showToast";
import {
  SOCIAL_ACCOUNT_DISCONNECT,
  SOCIAL_ACCOUNTS_GET_ADDED,
  SOCIAL_ACCOUNT_ADD_TIMESLOTS,
  SOCIAL_ACCOUNT_CLEAR_QUEUE,
  SOCIAL_ACCOUNT_DELETE_TIMESLOTS,
  SOCIAL_ACCOUNT_GET_TIMESLOTS,
  SOCIAL_ACCOUNT_GET_UTM,
  SOCIAL_ACCOUNT_SHUFFLE_QUEUE,
  SOCIAL_ACCOUNT_UPDATE_UTM,
  SOCIAL_ACCOUNT_GET_SHORTLINKS,
  SOCIAL_ACCOUNT_SET_SHORTLINKS,
  SOCIAL_ACCOUNT_GET_NOTIFICATION_SETTINGS,
  SOCIAL_ACCOUNT_SET_NOTIFICATION_SETTINGS,
  OAUTH_PINTEREST_LIST_BOARDS,
  OAUTH_ADD_CHILDREN_ACCOUNTS,
  OAUTH_GOOGLE_LIST_LOCATIONS,
  OAUTH_TIKTOK_MAIN_ACCOUNT,
  OAUTH_ADD_MAIN_ACCOUNT,
  OAUTH_YOUTUBE_MAIN_ACCOUNT,
  SOCIAL_ACCOUNT_SET_PUBLISH_BY,
  SOCIAL_ACCOUNT_GET_PAGES_FOR_GROUP,
  SOCIAL_ACCOUNTS_REFRESH_ACCOUNT_DATA,
  OAUTH_TWITTER_MAIN_ACCOUNT,
  OAUTH_LINKEDIN_LIST_COMPANIES,
  OAUTH_LINKEDIN_MAIN_ACCOUNT,
  OAUTH_FACEBOOK_LIST_PAGES,
  OAUTH_FACEBOOK_LIST_GROUPS,
  OAUTH_INSTAGRAM_LIST_BUSINESS_ACCOUNTS,
  OAUTH_INSTAGRAM_MAIN_ACCOUNT,
} from "./endpoints";
import { handleHTTPError } from "./errorHandlerActions";
import {
  refreshPostsView,
  handlePlannerResponse,
  setWorkflowType,
} from "./plannerActions";
import {
  getAnalyticsStatuses,
} from "./analyticsActions";
import {
  TOGGLE_SELECTED_SOCIAL_ACCOUNTS,
  SET_SOCIAL_ACCOUNTS,
  SET_SELECTED_SOCIAL_ACCOUNTS,
  SET_SELECTED_SOCIAL_ACCOUNT_SINGLE,
  SET_CHANNEL_SLOTS,
  SET_SELECTED_SOCIAL_ACCOUNTS_FOR_DRAFTS,
} from "./types";
import { getMetricsForSocialType } from "../_constants/AnalyticsAccountsMetrics";
import { ANALYTICS_STATUSES } from "../_constants/AnalyticsStatuses";

export const toggleSelectedSocialAccounts = (payload) => (dispatch) => {
  dispatch({
    type: TOGGLE_SELECTED_SOCIAL_ACCOUNTS,
    payload,
  });
};

export const setSelectedSocialAccounts = (ids) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_SOCIAL_ACCOUNTS,
    ids,
  });
};

export const setSelectedSocialAccountSingle = (id) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_SOCIAL_ACCOUNT_SINGLE,
    id,
  });
};

export const setSelectedSocialAccountsForDrafts = (ids) => (dispatch) => {
  dispatch({
    type: SET_SELECTED_SOCIAL_ACCOUNTS_FOR_DRAFTS,
    ids,
  });
};

export const setSocialAccounts = (accounts, options) => (dispatch) => {
  dispatch({
    type: SET_SOCIAL_ACCOUNTS,
    payload: accounts,
    options,
  });
};

export const fetchSocialAccounts = (options) => (dispatch) => {
  return new Promise(async (resolve, reject) => {
    try {
      const accountsRes = await axios.get(SOCIAL_ACCOUNTS_GET_ADDED);
      const accounts = accountsRes.data?.accountsList?.map((account) => ({
        ...account,
      })) || [];

      if (options?.withAnalyticsStatuses) {
        // intermidiate logic to fetch and map anlytics statuses, which are not served in standard accounts response;
        const socialAccountsIdsAvailableForAnalytics = accounts.filter(account =>
          getMetricsForSocialType(account?.social_type).length).map(account => account.id);

        const statusesResponse = await dispatch(getAnalyticsStatuses(socialAccountsIdsAvailableForAnalytics));
        const statuses = statusesResponse?.data?.statuses;

        accounts.forEach((account) => {
          if (statuses[account.id]) {
            account.analyticsStatus = statuses[account.id];
          } else {
            account.analyticsStatus = ANALYTICS_STATUSES.not_supported;
          }
        });
      }

      dispatch(setSocialAccounts(accounts, options));

      if (accountsRes.data?.workflowType) {
        // workflow type is sent along with social accounts, so we need to set it here;
        dispatch(setWorkflowType(accountsRes.data?.workflowType));
      }

      resolve(accounts);
    } catch (error) {
      dispatch(handleHTTPError(error));
      reject(error);
    }
  });
};

export const refreshSocialAccountData = (accountId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(SOCIAL_ACCOUNTS_REFRESH_ACCOUNT_DATA, {
        accountId
      })
      .then((res) => {
        resolve(res);
        handlePlannerResponse(res);
      })
      .catch((error) => {
        reject(error);
        dispatch(handleHTTPError(error));
      });
  });
};

export const disconnectSocialAccount = (accountId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(SOCIAL_ACCOUNT_DISCONNECT, {
        accountId,
      })
      .then((response) => {
        if (response.data?.status?.status) {
          showToast({
            description: response.data.status.message,
            title: "Success",
            status: "success",
          });

          dispatch(
            toggleSelectedSocialAccounts({
              ids: [accountId],
              value: false,
            })
          );

          dispatch(fetchSocialAccounts());
          resolve(response);
        } else {
          showToast({
            description: response.data?.status?.message,
            title: "Error",
            status: "error",
          });

          reject(new Error(response.data?.status?.message));
        }
      })
      .catch((error) => {
        reject(error);
        dispatch(handleHTTPError(error));
      });
  });
};

export const shuffleQueue = (accountId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(SOCIAL_ACCOUNT_SHUFFLE_QUEUE, {
        accountId: accountId,
      })
      .then((response) => {
        if (response.data?.status?.status) {
          showToast({
            description: response.data.status.message,
            title: "Success",
            status: "success",
          });

          dispatch(refreshPostsView());
          resolve(response);
        } else {
          showToast({
            description: response.data?.status?.message,
            title: "Error",
            status: "error",
          });

          reject(new Error(response.data?.status?.message));
        }
      })
      .catch((error) => {
        reject(error);
        dispatch(handleHTTPError(error));
      });
  });
};

export const clearQueue = (accountId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(SOCIAL_ACCOUNT_CLEAR_QUEUE, {
        accountId: accountId,
      })
      .then((response) => {
        if (response.data?.status?.status) {
          showToast({
            description: response.data.status.message,
            title: "Success",
            status: "success",
          });

          dispatch(refreshPostsView());
          resolve(response);
        } else {
          showToast({
            description: response.data?.status?.message,
            title: "Error",
            status: "error",
          });

          reject(new Error(response.data?.status?.message));
        }
      })
      .catch((error) => {
        reject(error);
        dispatch(handleHTTPError(error));
      });
  });
};

export const fetchTimepointsForSocialAccount = (channelId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .get(Format(SOCIAL_ACCOUNT_GET_TIMESLOTS, channelId))
      .then((res) => {
        dispatch({
          type: SET_CHANNEL_SLOTS,
          payload: res.data.queueTimes,
        });
        resolve(res);
      })
      .catch((error) => {
        reject(error);
        dispatch(handleHTTPError(error));
      });
  });
};

export const createTimepointsForSocialAccount =
  (channelId, slotBody) => (dispatch) => {
    return new Promise((resolve) => {
      axios
        .post(SOCIAL_ACCOUNT_ADD_TIMESLOTS, slotBody)
        .then((res) => {
          handlePlannerResponse(res, dispatch);
          dispatch(fetchTimepointsForSocialAccount(channelId));

          resolve(res);
        })
        .catch((error) => {
          dispatch(handleHTTPError(error));
        });
    });
  };

export const deleteTimepointsForSocialAccount = (body) => (dispatch) => {
  return new Promise((resolve) => {
    axios
      .post(SOCIAL_ACCOUNT_DELETE_TIMESLOTS, body)
      .then((res) => {
        handlePlannerResponse(res, dispatch);
        dispatch(fetchTimepointsForSocialAccount(body.account_id));

        resolve(res);
      })
      .catch((error) => {
        dispatch(handleHTTPError(error));
      });
  });
};

export const getUTMForSocialAccount = (accountId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .get(Format(SOCIAL_ACCOUNT_GET_UTM, accountId))
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
        dispatch(handleHTTPError(error));
      });
  });
};

export const updateUTMForSocialAccount = (body) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(SOCIAL_ACCOUNT_UPDATE_UTM, body)
      .then((res) => {
        showToast({
          description: res.data.results[0].message,
          title: "Success",
          status: "success",
        });

        resolve(res);
      })
      .catch((error) => {
        reject(error);
        dispatch(handleHTTPError(error));
      });
  });
};

export const getShortlinksForSocialAccount = (accountId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .get(Format(SOCIAL_ACCOUNT_GET_SHORTLINKS, accountId))
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
        dispatch(handleHTTPError(error));
      });
  });
};

export const updateShortlinksForSocialAccount = (data) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(SOCIAL_ACCOUNT_SET_SHORTLINKS, data)
      .then((res) => {
        handlePlannerResponse(res);
        resolve(res);
      })
      .catch((error) => {
        reject(error);
        dispatch(handleHTTPError(error));
      });
  });
};

export const getPagesForGroup = (accountId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .get(Format(SOCIAL_ACCOUNT_GET_PAGES_FOR_GROUP, accountId))
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
        dispatch(handleHTTPError(error));
      });
  });
};

export const updatePublishByForSocialAccount = (data) => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .post(SOCIAL_ACCOUNT_SET_PUBLISH_BY, data)
      .then((res) => {
        handlePlannerResponse(res);
        resolve(res);
      })
      .catch((error) => {
        reject(error);
        dispatch(handleHTTPError(error));
      });
  });
};

export const getSocialAccountsNotificationSettings = () => (dispatch) => {
  return new Promise((resolve, reject) => {
    axios
      .get(SOCIAL_ACCOUNT_GET_NOTIFICATION_SETTINGS)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
        dispatch(handleHTTPError(error));
      });
  });
};

export const updateSocialAccountsNotificationSettings = (data) =>
  new Promise((resolve, reject) => {
    axios
      .post(SOCIAL_ACCOUNT_SET_NOTIFICATION_SETTINGS, data)
      .then((res) => {
        handlePlannerResponse(res);

        if (res.data.status === 1) {
          resolve(res);
          return;
        }
        reject(res.status.message);
      })
      .catch((error) => {
        reject(error);
      });
  });

export const addMainAccount =
  ({ id }) =>
  (dispatch) => {
    return new Promise((resolve, reject) => {
      axios
        .post(OAUTH_ADD_MAIN_ACCOUNT, { id })
        .then((res) => {
          if (res.data.status && res.data.status !== 1) {
            handlePlannerResponse(res, dispatch);
          }

          resolve(res);
        })
        .catch((error) => {
          handlePlannerResponse(error.response, dispatch);
          reject(error);
        });
    });
  };

export const addChildrenAccounts =
  ({ id, items }) =>
  (dispatch) => {
    return new Promise((resolve, reject) => {
      axios
        .post(OAUTH_ADD_CHILDREN_ACCOUNTS, { id, items })
        .then((res) => {
          if (res.data.status && res.data.status !== 1) {
            handlePlannerResponse(res, dispatch);
          }

          resolve(res);
        })
        .catch((error) => {
          handlePlannerResponse(error.response, dispatch);
          reject(error);
        });
    });
  };

export const getFacebookPages =
  ({ code, callback_url }) =>
  (dispatch) => {
    const params = {
      code,
      callback_url,
    };

    return new Promise((resolve, reject) => {
      axios
        .get(OAUTH_FACEBOOK_LIST_PAGES, {
          params,
        })
        .then((res) => {
          if (res.data.status && res.data.status !== 1) {
            handlePlannerResponse(res, dispatch);
          }

          resolve(res);
        })
        .catch((error) => {
          handlePlannerResponse(error.response, dispatch);
          reject(error);
        });
    });
  };

export const getFacebookGroups =
  ({ code, callback_url }) =>
  (dispatch) => {
    const params = {
      code,
      callback_url,
    };

    return new Promise((resolve, reject) => {
      axios
        .get(OAUTH_FACEBOOK_LIST_GROUPS, {
          params,
        })
        .then((res) => {
          if (res.data.status && res.data.status !== 1) {
            handlePlannerResponse(res, dispatch);
          }

          resolve(res);
        })
        .catch((error) => {
          handlePlannerResponse(error.response, dispatch);
          reject(error);
        });
    });
  };

export const getInstagramBusinessAccounts =
  ({ code, callback_url }) =>
  (dispatch) => {
    const params = {
      code,
      callback_url,
    };

    return new Promise((resolve, reject) => {
      axios
        .get(OAUTH_INSTAGRAM_LIST_BUSINESS_ACCOUNTS, {
          params,
        })
        .then((res) => {
          if (res.data.status && res.data.status !== 1) {
            handlePlannerResponse(res, dispatch);
          }

          resolve(res);
        })
        .catch((error) => {
          handlePlannerResponse(error.response, dispatch);
          reject(error);
        });
    });
  };

export const getLinkedInCompanies =
  ({ code, callback_url }) =>
  (dispatch) => {
    const params = {
      code,
      callback_url,
    };

    return new Promise((resolve, reject) => {
      axios
        .get(OAUTH_LINKEDIN_LIST_COMPANIES, {
          params,
        })
        .then((res) => {
          if (res.data.status && res.data.status !== 1) {
            handlePlannerResponse(res, dispatch);
          }

          resolve(res);
        })
        .catch((error) => {
          handlePlannerResponse(error.response, dispatch);
          reject(error);
        });
    });
  };

export const getGoogleLocations =
  ({ code, callback_url }) =>
  (dispatch) => {
    const params = {
      code,
      callback_url,
    };

    return new Promise((resolve, reject) => {
      axios
        .get(OAUTH_GOOGLE_LIST_LOCATIONS, {
          params,
        })
        .then((res) => {
          if (res.data.status && res.data.status !== 1) {
            handlePlannerResponse(res, dispatch);
          }

          resolve(res);
        })
        .catch((error) => {
          handlePlannerResponse(error.response, dispatch);
          reject(error);
        });
    });
  };

export const getPinterestBoards =
  ({ code, callback_url }) =>
  (dispatch) => {
    const params = {
      code,
      callback_url,
    };

    return new Promise((resolve, reject) => {
      axios
        .get(OAUTH_PINTEREST_LIST_BOARDS, {
          params,
        })
        .then((res) => {
          if (res.data.status && res.data.status !== 1) {
            handlePlannerResponse(res, dispatch);
          }

          resolve(res);
        })
        .catch((error) => {
          handlePlannerResponse(error.response, dispatch);
          reject(error);
        });
    });
  };

export const addLinkedInAccount =
  ({ code, callback_url }) =>
  (dispatch) => {
    const params = {
      code,
      callback_url,
    };

    return new Promise(async (resolve, reject) => {
      try {
        const authResponse = await axios.get(OAUTH_LINKEDIN_MAIN_ACCOUNT, {
          params,
        });

        if (authResponse.data.status && authResponse.data.status !== 1) {
          handlePlannerResponse(authResponse, dispatch);
          resolve(authResponse);
        } else {
          const addResponse = await dispatch(addMainAccount(authResponse.data));
          resolve(addResponse);
        }
      } catch (error) {
        handlePlannerResponse(error.response, dispatch);
        reject(error);
      }
    });
  };

export const addTikTokAccount =
  ({ code }) =>
  (dispatch) => {
    return new Promise(async (resolve, reject) => {
      try {
        const authResponse = await axios.get(
          `${OAUTH_TIKTOK_MAIN_ACCOUNT}?code=${code}`
        );

        if (authResponse.data.status && authResponse.data.status !== 1) {
          handlePlannerResponse(authResponse, dispatch);
          resolve(authResponse);
        } else {
          const addResponse = await dispatch(addMainAccount(authResponse.data));
          resolve(addResponse);
        }
      } catch (error) {
        handlePlannerResponse(error.response, dispatch);
        reject(error);
      }
    });
  };

export const addInstagramAccount =
  ({ code, callback_url }) =>
  (dispatch) => {
    const params = {
      code,
      callback_url,
    };

    return new Promise(async (resolve, reject) => {
      try {
        const authResponse = await axios.get(OAUTH_INSTAGRAM_MAIN_ACCOUNT, {
          params,
        });

        if (authResponse.data.status && authResponse.data.status !== 1) {
          handlePlannerResponse(authResponse, dispatch);
          resolve(authResponse);
        } else {
          const addResponse = await dispatch(addMainAccount(authResponse.data));
          resolve(addResponse);
        }
      } catch (error) {
        handlePlannerResponse(error.response, dispatch);
        reject(error);
      }
    });
  };

export const addYouTubeAccount =
  ({ code, callback_url }) =>
  (dispatch) => {
    const params = {
      code,
      callback_url,
    };

    return new Promise(async (resolve, reject) => {
      try {
        const authResponse = await axios.get(OAUTH_YOUTUBE_MAIN_ACCOUNT, {
          params,
        });

        if (authResponse.data.status && authResponse.data.status !== 1) {
          handlePlannerResponse(authResponse, dispatch);
          resolve(authResponse);
        } else {
          const addResponse = await dispatch(addMainAccount(authResponse.data));
          resolve(addResponse);
        }
      } catch (error) {
        handlePlannerResponse(error.response, dispatch);
        reject(error);
      }
    });
  };

export const addTwitterAccount =
  ({ oauth_token, oauth_verifier, follow }) =>
  (dispatch) => {
    const params = {
      oauth_token,
      oauth_verifier,
      follow,
    };

    return new Promise(async (resolve, reject) => {
      try {
        const authResponse = await axios.get(OAUTH_TWITTER_MAIN_ACCOUNT, {
          params,
        });

        if (authResponse.data.status && authResponse.data.status !== 1) {
          handlePlannerResponse(authResponse, dispatch);
          resolve(authResponse);
        } else {
          const addResponse = await dispatch(addMainAccount(authResponse.data));
          resolve(addResponse);
        }
      } catch (error) {
        handlePlannerResponse(error.response, dispatch);
        reject(error);
      }
    });
  };
